import { useState, useEffect, useCallback, useId } from "react";

type Option = string | { label: string; value: string };

type Field = {
  name: string;
  label: string;
  required?: boolean;
  options?: Option[];
};

export default function ApplicantForm({
  fields,
  onSave,
  onCancel,
  initialValues,
  note,
  readOnly,
}: {
  fields: Field[];
  onSave: (data: any) => void;
  onCancel: () => void;
  initialValues?: Record<string, any>;
  note?: string;
  readOnly?: boolean;
}) {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const formId = useId();

  const normalizeInitialValues = useCallback(
    (obj: any, prefix = "", res: any = {}) => {
      for (const key in obj) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value)
        ) {
          normalizeInitialValues(value, newKey, res);
        } else {
          res[newKey] = value;
        }
      }
      return res;
    },
    [],
  );

  useEffect(() => {
    if (initialValues) {
      setFormValues(normalizeInitialValues(initialValues));
      setErrors({});
    }
  }, [initialValues, normalizeInitialValues]);

  function setNestedValue(obj: any, path: string, value: any) {
    const keys = path.split(".");
    let current = obj;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value;
      } else {
        current[key] = current[key] || {};
        current = current[key];
      }
    });
  }

  const handleChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleSubmit = () => {
    const newErrors: Record<string, boolean> = {};

    fields.forEach((field) => {
      if (field.required) {
        const value = formValues[field.name];
        if (!value || String(value).trim() === "") {
          newErrors[field.name] = true;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const result: any = {};
    Object.entries(formValues).forEach(([key, value]) => {
      setNestedValue(result, key, value);
    });

    onSave(result);
  };

  return (
    <div className="space-y-6">
      {note && <div className="text-sm text-gray-600">{note}</div>}

      {fields.map((field) => (
        <div key={field.name} className="flex flex-col gap-1">
          <label htmlFor={`${formId}-${field.name}`} className="text-sm font-medium text-gray-700">
            {field.label}
            {field.required && " *"}
          </label>

          {field.options ? (
            <select
              id={`${formId}-${field.name}`}
              value={formValues[field.name] || ""}
              onChange={(e) => handleChange(field.name, e.target.value)}
              disabled={readOnly}
              className={`w-full rounded-md border px-3 py-2 text-sm transition focus:border-dantzBlue focus:ring-1 focus:ring-dantzBlue ${
                errors[field.name]
                  ? "border-red-500 bg-white"
                  : "border-gray-300 bg-white"
              } ${readOnly ? "bg-gray-100 text-gray-500" : ""}`}
            >
              <option value="">Select</option>
              {field.options.map((opt) =>
                typeof opt === "string" ? (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ) : (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ),
              )}
            </select>
          ) : (
            <input
              id={`${formId}-${field.name}`}
              type="text"
              value={formValues[field.name] || ""}
              onChange={(e) => handleChange(field.name, e.target.value)}
              disabled={readOnly}
              className={`w-full rounded-md border px-3 py-2 text-sm transition focus:border-dantzBlue focus:ring-1 focus:ring-dantzBlue ${
                errors[field.name]
                  ? "border-red-500 bg-white"
                  : "border-gray-300 bg-white"
              } ${readOnly ? "bg-gray-100 text-gray-500" : ""}`}
            />
          )}

          {errors[field.name] && (
            <span className="text-xs text-red-600">This field is required</span>
          )}
        </div>
      ))}

      <div className="flex flex-wrap gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 transition hover:bg-gray-50"
        >
          Cancel
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm rounded-lg font-medium text-white bg-dantzBlue transition hover:bg-blue-700"
          >
            Save
          </button>
        )}
      </div>
    </div>
  );
}
